// src/pages/admin/board/AdminBoardPostManage.tsx

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '../../../api'

interface Board {
  id: number
  slug: string
  name: string
  theme: string
  has_file: number
  has_image: number
}

interface BoardPost {
  id: number
  board_id: number
  title: string
  is_visible: 0 | 1
  created_at: string
}

interface BoardPostListResponse {
  total: number
  page: number
  size: number
  items: BoardPost[]
}

export default function AdminBoardPostManage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [board, setBoard] = useState<Board | null>(null)
  const [posts, setPosts] = useState<BoardPost[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [size] = useState(20)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchBoard()
  }, [slug])

  useEffect(() => {
    if (board) {
      fetchPosts()
    }
  }, [board, page])

  const fetchBoard = async () => {
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/board/${slug}`))
      if (!res.ok) throw new Error('게시판 조회 실패')
      setBoard(await res.json())
    } catch (err) {
      alert('게시판을 찾을 수 없습니다')
      navigate('/admin/board')
    }
  }

  const fetchPosts = async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/board/${slug}/post?page=${page}&size=${size}`)
      )
      if (!res.ok) throw new Error('조회 실패')
      const data: BoardPostListResponse = await res.json()
      setPosts(data.items)
      setTotal(data.total)
    } catch (err) {
      console.error(err)
      alert('게시글 목록을 불러오지 못했습니다')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (post: BoardPost) => {
    if (!window.confirm(`"${post.title}" 게시글을 삭제하시겠습니까?`)) return

    try {
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/board/${slug}/post/${post.id}`),
        { method: 'DELETE' }
      )
      if (!res.ok) throw new Error('삭제 실패')
      alert('게시글이 삭제되었습니다')
      fetchPosts()
    } catch (err) {
      alert('삭제에 실패했습니다')
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  }

  const totalPages = Math.ceil(total / size)

  if (!board) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fdf8f0]">
        <p className="text-gray-400">불러오는 중...</p>
      </div>
    )
  }

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-6">
      <div className="max-w-[1200px] mx-auto">
        {/* 헤더 */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <button
              onClick={() => navigate('/admin/board')}
              className="text-gray-400 hover:text-gray-600 text-[13px]"
            >
              ← 게시판 목록
            </button>
          </div>
          <div className="flex justify-between items-center">
            <h1 className="text-[22px] font-extrabold text-[#2a1f1a]">
              {board.name} 게시판
            </h1>
            <button
              onClick={() => navigate(`/admin/board/${slug}/post/new`)}
              className="bg-[#887267] text-white text-[13px] font-semibold px-4 py-2 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors"
            >
              + 새 게시글
            </button>
          </div>
        </div>

        {/* 게시글 목록 */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <table className="w-full">
            <thead className="bg-[#ede0d8] border-b border-[#e8e0dc]">
              <tr>
                <th className="text-left px-5 py-3 text-[12px] text-[#5a3d32] font-medium w-[80px]">
                  ID
                </th>
                <th className="text-left px-5 py-3 text-[12px] text-[#5a3d32] font-medium">
                  제목
                </th>
                <th className="hidden md:table-cell text-center px-5 py-3 text-[12px] text-[#5a3d32] font-medium w-[80px]">
                  상태
                </th>
                <th className="hidden lg:table-cell text-center px-5 py-3 text-[12px] text-[#5a3d32] font-medium w-[120px]">
                  작성일
                </th>
                <th className="text-center px-5 py-3 text-[12px] text-[#5a3d32] font-medium w-[180px]">
                  관리
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-gray-400 text-[14px]">
                    불러오는 중...
                  </td>
                </tr>
              ) : posts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-gray-400 text-[14px]">
                    등록된 게시글이 없습니다
                  </td>
                </tr>
              ) : (
                posts.map((post) => (
                  <tr
                    key={post.id}
                    onClick={() => navigate(`/admin/board/${slug}/post/${post.id}`)}
                    className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors cursor-pointer"
                  >
                    <td className="px-5 py-4 text-[13px] text-[#333]">
                      {post.id}
                    </td>
                    <td className="px-5 py-4 text-[13px] text-[#333]">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{post.title}</span>
                        {post.is_visible === 0 && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-[11px] rounded">
                            비공개
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="hidden md:table-cell px-5 py-4 text-center">
                      {post.is_visible === 1 ? (
                        <span className="text-green-500 text-[18px]">✓</span>
                      ) : (
                        <span className="text-gray-300 text-[18px]">✗</span>
                      )}
                    </td>
                    <td className="hidden lg:table-cell px-5 py-4 text-center text-[13px] text-[#666]">
                      {formatDate(post.created_at)}
                    </td>
                    <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() =>
                            navigate(`/admin/board/${slug}/post/${post.id}`)
                          }
                          className="px-3 py-1 text-[12px] border border-[#887267] text-[#887267] rounded hover:bg-[#f5f2f0] transition-colors whitespace-nowrap"
                        >
                          수정
                        </button>
                        <button
                          onClick={() => handleDelete(post)}
                          className="px-3 py-1 text-[12px] border border-red-200 text-red-400 rounded hover:bg-red-50 transition-colors whitespace-nowrap"
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 py-4 border-t border-[#f5f2f0]">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className={`px-3 py-1 text-[13px] rounded ${
                  page === 1
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-[#887267] hover:bg-[#f5f2f0]'
                }`}
              >
                ◀
              </button>
              <span className="text-[13px] text-[#666]">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className={`px-3 py-1 text-[13px] rounded ${
                  page === totalPages
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-[#887267] hover:bg-[#f5f2f0]'
                }`}
              >
                ▶
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}